import React from "react";
import SensitiveInfo, { SensitiveInfoUpdate } from "./SensitiveInfo";
import { writeFieldToForm } from "./formFieldHelpers";
import FormConstants from "./DataEntryFieldsConstants";
import {
    SensitiveInfo as SensitiveInfoModel,
    SensitiveInfoRelease,
    SensitiveInfoType,
    SensitiveInfoUpdateAPI,
} from "../../../models/transaction";
import { FieldGroupRow, FieldGroupRowStatus } from "../../../models/intake";

export interface SensitiveInfoFieldProps {
    /** props.transactionDetails.sensitiveInfo - same source both classic and Quick Entry read. */
    readonly data: readonly SensitiveInfoModel[];
    readonly transactionId?: string;
    readonly dispatch: Function;
    readonly formName?: string;
    readonly errorMsg?: string;
    /** Hides the field entirely - classic pane's isVisibleSensitiveInfoToRelease gate. */
    readonly visible: boolean;
    readonly saveAndUpdateSensitiveInfo?: (data: SensitiveInfoUpdateAPI) => void;
    /** AB#35086: audit call sitting alongside saveAndUpdateSensitiveInfo - see applySensitiveInfoUpdate's own comment for why they're wired together. */
    readonly putAuditTransactionSensitiveInfo?: (transactionId: string, recordContains: string) => void;
}

/**
 * Shared Sensitive Information field (Spec #34098 / F1a). Wraps the existing
 * SensitiveInfo.tsx checkbox grid. Unlike RecordsRequestedField this field
 * has no component-local state of its own - every update is derived
 * directly from props.data (transactionDetails.sensitiveInfo) and persisted
 * immediately via saveAndUpdateSensitiveInfo, the exact pattern
 * DataEntryDetailsRightPane.tsx's own saveSensitiveInfo used inline before
 * this extraction. Both classic RightPane and Quick Entry mount this same
 * component now.
 */
const SensitiveInfoField: React.FC<SensitiveInfoFieldProps> = (props) => {
    if (!props.visible) {
        return null;
    }

    const formName = props.formName ?? FormConstants.DATA_ENTRY_FIELDS_FORM;

    const onChange = (update: readonly SensitiveInfoUpdate[]) => {
        applySensitiveInfoUpdate(props.data, update, props.transactionId, props.dispatch, props.saveAndUpdateSensitiveInfo, formName, props.putAuditTransactionSensitiveInfo);
    };

    return (
        <SensitiveInfo
            data={props.data}
            onChange={onChange}
            errorMsg={props.errorMsg}
        />
    );
};

export default SensitiveInfoField;

export const SENSITIVE_INFO_ROW_ID = "sensitiveInfo";

/** Applies a set of checkbox changes: resolved sync-error flags + immediate API persistence. */
export function buildUpdatedSensitiveInfo(data: readonly SensitiveInfoModel[], update: readonly SensitiveInfoUpdate[]): readonly SensitiveInfoModel[] {
    return data.map(item => {
        const newItem = update.find(upd => upd.updatedItemId === item.id);
        return newItem ? {
            ...item,
            stateID: newItem.checked ? SensitiveInfoRelease.Authorized : SensitiveInfoRelease.Prohibited,
        } :
            item.stateID === SensitiveInfoRelease.NotMentioned ? { ...item, stateID: SensitiveInfoRelease.Prohibited } :
                item;
    });
}

function buildSensitiveInfoUpdatePayload(
    data: readonly SensitiveInfoModel[],
    update: readonly SensitiveInfoUpdate[],
    transactionId: string | undefined,
): SensitiveInfoUpdateAPI {
    const updatedValues = buildUpdatedSensitiveInfo(data, update);
    const genetic = updatedValues.find(item => item.id === SensitiveInfoType.GeneticInformation)?.stateID;
    const hiv = updatedValues.find(item => item.id === SensitiveInfoType.HivAids)?.stateID;
    const mhlt = updatedValues.find(item => item.id === SensitiveInfoType.MentalHealth)?.stateID;
    const drug = updatedValues.find(item => item.id === SensitiveInfoType.SubstanceAbuse)?.stateID;
    const std = updatedValues.find(item => item.id === SensitiveInfoType.Std)?.stateID;

    const sensitiveInfoEntries = data.map(item => {
        const updatedRecord = update.find(upd => upd.updatedItemId === item.id);
        return {
            model: "sensitiveInfoItem",
            id: item.id,
            name: item.name,
            stateID: updatedRecord
                ? (updatedRecord.checked ? SensitiveInfoRelease.Authorized : SensitiveInfoRelease.Prohibited)
                : (item.stateID === SensitiveInfoRelease.NotMentioned ? SensitiveInfoRelease.Prohibited : item.stateID),
        };
    });

    return {
        UpdatePayload: {
            type: "",
            data: {
                transactionId: transactionId ?? "",
                userID: global.userID,
                geneticSensitiveInfo: genetic,
                hivAidsSensitiveInfo: hiv,
                mentalHealthSensitiveInfo: mhlt,
                substanceAbuseSensitiveInfo: drug,
                sexuallyTransmittedDiseaseSensitiveInfo: std,
            },
            update: false,
        },
        InfoList: sensitiveInfoEntries,
    } as SensitiveInfoUpdateAPI;
}

/**
 * AB#35086: PUT /api/auditTrail/{id}/sensitiveinfo (AuditTask.TransactionContainsSensitiveInfo)
 * already existed end to end (Redux action/saga/API, AuditController.cs) but nothing ever called
 * it - dead wiring, in both Classic and Quick Entry (this field is shared by both). recordContains
 * is sent as the literal audit message text (AuditController.cs's AuditTransactionSensitiveInfo),
 * so it has to describe every category's resulting state, not just which one changed.
 */
export function buildSensitiveInfoAuditMessage(items: readonly Pick<SensitiveInfoModel, "name" | "stateID">[]): string {
    return items.map(item => {
        const state = item.stateID === SensitiveInfoRelease.Authorized ? "Authorized"
            : item.stateID === SensitiveInfoRelease.Prohibited ? "Prohibited"
                : "Not Mentioned";
        return `${item.name}: ${state}`;
    }).join(", ");
}

/**
 * Shared entry point for "apply a batch of Sensitive Info checkbox changes":
 * writes the DISCLOSE_ALL_INFO/DONT_DISCLOSE_ANY_INFO derived flags into the
 * form and persists via saveAndUpdateSensitiveInfo. Used by this field's own
 * onChange AND by DataEntryDetailsRightPane.tsx's onChangeRequestType (the
 * AUDIT request-type auto-authorize-all cascade) - both need the exact same
 * derived-payload logic, not just the checkbox-grid UI.
 *
 * AB#35086: putAuditTransactionSensitiveInfo fires right alongside the real
 * save, same as every other working AddTaskAuditTrail call site in this app
 * (persist + audit live in the one method/call that does the save) - not a
 * second, independent mechanism.
 */
export function applySensitiveInfoUpdate(
    data: readonly SensitiveInfoModel[],
    update: readonly SensitiveInfoUpdate[],
    transactionId: string | undefined,
    dispatch: Function,
    saveAndUpdateSensitiveInfo: ((data: SensitiveInfoUpdateAPI) => void) | undefined,
    formName: string = FormConstants.DATA_ENTRY_FIELDS_FORM,
    putAuditTransactionSensitiveInfo?: (transactionId: string, recordContains: string) => void,
): void {
    const updatedValues = buildUpdatedSensitiveInfo(data, update);

    writeFieldToForm(dispatch, FormConstants.DISCLOSE_ALL_INFO, updatedValues.every(item =>
        item.stateID === SensitiveInfoRelease.Authorized), formName);
    writeFieldToForm(dispatch, FormConstants.DONT_DISCLOSE_ANY_INFO, updatedValues.every(item =>
        item.stateID === SensitiveInfoRelease.Prohibited), formName);

    saveAndUpdateSensitiveInfo?.(buildSensitiveInfoUpdatePayload(data, update, transactionId));
    putAuditTransactionSensitiveInfo?.(transactionId ?? "", buildSensitiveInfoAuditMessage(updatedValues));
}

/**
 * Bug fix (Spec #34102 / F1d, direct user report): "Switching to Classic
 * Mode should NOT carry over data entered in Quick Entry" - but Sensitive
 * Information does, because unlike every other Quick Entry field (a
 * redux-form draft until Confirm-All/Create Transaction persists it), this
 * field persists immediately via saveAndUpdateSensitiveInfo (see this file's
 * own doc comment above). Classic Mode reads the same, now-mutated
 * transactionDetails.sensitiveInfo directly, so it faithfully (and, for this
 * one boundary, wrongly) displays whatever Quick Entry already wrote.
 *
 * Reconstructs the exact SensitiveInfoUpdateAPI payload for reverting the
 * ACTUAL persisted record back to a snapshot taken before the Quick Entry
 * session touched it (DataEntryDetails.tsx's own
 * revertQuickEntryRecordsAndSensitiveInfo, fired from the "Switch to Classic
 * Mode" confirm handler) - deliberately built straight from the snapshot's
 * own stateID values, NOT via buildSensitiveInfoUpdatePayload's update-array
 * indirection (that function only knows how to express "checked"/
 * "unchecked" for items an update touched, which would collapse an
 * untouched original SensitiveInfoRelease.NotMentioned item to Prohibited -
 * wrong for a snapshot that may genuinely have been NotMentioned).
 */
export function buildSensitiveInfoRevertPayload(original: readonly SensitiveInfoModel[], transactionId: string | undefined): SensitiveInfoUpdateAPI {
    const genetic = original.find(item => item.id === SensitiveInfoType.GeneticInformation)?.stateID;
    const hiv = original.find(item => item.id === SensitiveInfoType.HivAids)?.stateID;
    const mhlt = original.find(item => item.id === SensitiveInfoType.MentalHealth)?.stateID;
    const drug = original.find(item => item.id === SensitiveInfoType.SubstanceAbuse)?.stateID;
    const std = original.find(item => item.id === SensitiveInfoType.Std)?.stateID;

    return {
        UpdatePayload: {
            type: "",
            data: {
                transactionId: transactionId ?? "",
                userID: global.userID,
                geneticSensitiveInfo: genetic,
                hivAidsSensitiveInfo: hiv,
                mentalHealthSensitiveInfo: mhlt,
                substanceAbuseSensitiveInfo: drug,
                sexuallyTransmittedDiseaseSensitiveInfo: std,
            },
            update: false,
        },
        InfoList: original.map(item => ({
            model: "sensitiveInfoItem",
            id: item.id,
            name: item.name,
            stateID: item.stateID,
        })),
    } as SensitiveInfoUpdateAPI;
}

/**
 * Builds the FieldGroupRow for the Sensitive Information row. Confirmed once
 * every item has been explicitly authorized or denied (no NotMentioned left)
 * - mirrors isSensitiveInfoSelected's own "every item resolved" requirement
 * (validations/localValidations.ts) - AND the specialist has closed the row.
 *
 * AB#38735 (Mike Hamilton, 2026-08-31): AMBER is this row's unresolved tier, not red. The
 * extraction over-declares protected categories on 57.1% of documents (Bug #38735
 * Microsoft.VSTS.CMMI.Analysis Part 2), so a checklist the specialist has not signed off on is
 * an unverified suggestion - which is what LowConfidence means - and never the same thing as an
 * unanswered required field. Red said "you have not filled this in" about a row that was
 * already full of the AI's answers.
 *
 * Three states, in order:
 *
 *   data.length === 0    NeedsEntry. The questionnaire has not loaded, so there is nothing on
 *                        screen to review. This carve-out is load-bearing, not tidiness: an
 *                        amber row over an EMPTY checklist has no exit - nothing to answer,
 *                        no Acknowledge control (this row is self-resolving) - so Confirm All
 *                        could never enable and the transaction would be uncompletable. That
 *                        is the Critical raised against PR 9995, reached from the other side.
 *   resolved && closed   Confirmed. Specialist action resolves the row; decision 12 is explicit
 *                        that a permanently amber row is the defect, not the requirement.
 *   otherwise            LowConfidence.
 *
 * `isOpenForReview` is AB#35073's quickEntryEditingRowIds membership, which the seed joins the
 * row to. It used to reach the row via getQuickEntryRows' generic Confirmed-while-editing
 * downgrade (to NeedsEntry + isCompleteWhileEditing); moving the decision here means that
 * branch no longer fires for this row and the flag is no longer set on it.
 * isFieldGroupRowConfirmDisabled blocks Confirm only on NeedsEntry, so an amber row keeps its
 * own Confirm button live without needing the flag.
 */
export function mapSensitiveInfoToFieldGroupRow(badgeNumber: number, data: readonly SensitiveInfoModel[], isOpenForReview: boolean = false): FieldGroupRow {
    // AB#37856 (round-2 review S5): uses the shared isSensitiveInfoResolved rather than its own
    // inline copy. This row's Confirmed status IS the definition the AB#37856 safety gate and the
    // correction value claim to match, so it has to come from the same place or the file asserts a
    // single source of truth it doesn't have. Verbatim swap - the helper is the same expression
    // this function inlined.
    const isResolved = isSensitiveInfoResolved(data);
    const authorizedCount = data.filter(item => item.stateID === SensitiveInfoRelease.Authorized).length;

    let value: string | null = null;
    if (isResolved) {
        value = authorizedCount === 0 ? "None authorized"
            : authorizedCount === data.length ? "All authorized"
                : `${authorizedCount} of ${data.length} authorized`;
    }

    return {
        id: SENSITIVE_INFO_ROW_ID,
        badgeNumber,
        status: data.length === 0 ? FieldGroupRowStatus.NeedsEntry
            : isResolved && !isOpenForReview ? FieldGroupRowStatus.Confirmed
                : FieldGroupRowStatus.LowConfidence,
        label: "Sensitive Information",
        value,
        isAiPreSelected: false,
    };
}

/**
 * AB#37856. The AI extraction's `sensitiveInfoDeclarations` vocabulary, mapped to the
 * SensitiveInfoType ids it authorizes. A closed six-value enum, constrained server-side
 * by the Intake API's own schema (`intakeSchema.json`) with the exact spelling and casing
 * pinned by `PromptV7.txt` - so this is an ALLOW-LIST, not a parser: the enum exists as a
 * PHI-channel control (both multi-select fields were free-text before AB#35348, into which
 * the model demonstrably echoed raw document content), and anything outside it is dropped
 * rather than carried into state or the corrections payload.
 *
 * Keyed on the LOWERCASED enum value, because the two systems disagree on casing for
 * exactly one category: the extraction says "Mental health", ROIS's own display name is
 * "Mental Health". A Map rather than an object literal so a drifted declaration that
 * happens to name an Object.prototype member ("constructor") can't resolve to anything.
 *
 * "Disclosed" is deliberately ABSENT - it is the extraction's "sensitive information is
 * released but no category was named" sentinel, so it authorizes nothing and the
 * specialist decides from the document (developer decision, 2026-08-11).
 */
const AI_SENSITIVE_INFO_VOCABULARY: ReadonlyMap<string, SensitiveInfoType> = new Map([
    ["genetic information", SensitiveInfoType.GeneticInformation],
    ["hiv/aids", SensitiveInfoType.HivAids],
    ["mental health", SensitiveInfoType.MentalHealth],
    ["substance abuse", SensitiveInfoType.SubstanceAbuse],
    ["sexually transmitted disease", SensitiveInfoType.Std],
]);

/**
 * The declared categories that map onto ids actually present in this transaction's item
 * list. Matching is EXACT (case-insensitively) on the whole value and then by id - never
 * by substring and never by name, unlike RecordsRequestedField's matchAiReleaseItemIds:
 * a substring match would miss "Mental health"/"Mental Health" on case and would read
 * "Disclosed" as a category miss rather than the distinct sentinel it is, and the ROIS
 * display names are not a stable key.
 *
 * Shared by the seed (DataEntryDetails.seedExtractedSensitiveInfo), the doc-sourced badge
 * gate and the correction payload's aiValue, so the four can never drift apart.
 */
/**
 * Security review L3. `models/intake.ts` parses this field with `as string[]` plus a truthiness
 * filter - a CAST, not a validated `string[]` - so upstream contract drift can put a non-string
 * element in the array, and calling `.trim()` on it throws. That matters most on the render path:
 * `isQuickEntryValueDocSourced` runs inside `getQuickEntryRows` during render, so a throw there
 * takes out the whole Quick Entry pane rather than just losing a value.
 *
 * One normalizer for every consumer, so the guard can't be added to some call sites and forgotten
 * on others. A non-string yields null, which every caller already treats as "no match".
 */
function normalizeAiDeclaration(value: unknown): string | null {
    return typeof value === "string" ? value.trim().toLowerCase() : null;
}

export function matchAiSensitiveInfoIds(
    declarations: readonly string[],
    data: readonly SensitiveInfoModel[],
): Set<SensitiveInfoType> {
    const matched = new Set<SensitiveInfoType>();
    declarations.forEach(declaration => {
        const key = normalizeAiDeclaration(declaration);
        const id = key === null ? undefined : AI_SENSITIVE_INFO_VOCABULARY.get(key);
        if (id !== undefined && data.some(item => item.id === id)) {
            matched.add(id);
        }
    });
    return matched;
}

/**
 * AB#37856 (round-1 review W1). Whether the specialist has actually ANSWERED the
 * questionnaire - every category explicitly Authorized or Prohibited, nothing left
 * NotMentioned. The same condition mapSensitiveInfoToFieldGroupRow uses to decide Confirmed and
 * that localValidations' isSensitiveInfoSelected requires; exported so the safety-relevant
 * consumers can share one definition rather than re-deriving it.
 *
 * NOTE (2026-08-12): this is no longer sufficient on its own to mean "the specialist decided
 * these". The seed now pre-fills every category, so a wholly unreviewed row satisfies this
 * predicate. What distinguishes AI-proposed from specialist-accepted is the row being open for
 * review - AB#35073's quickEntryEditingRowIds, which the seed joins the row to - so the
 * safety-relevant consumers pair this with that check rather than relying on it alone. See
 * `sensitiveInfoAwaitingReview` at the authorizedEntities gate in DataEntryDetails' render.
 */
/**
 * The raw stateID values that count as an explicit specialist answer: Authorized or Prohibited, in
 * the numeric form the model declares and the string form a JSON int column could arrive as.
 *
 * An ALLOW-LIST on the raw value, deliberately not `Number()` coercion (security review L1 then L4).
 * Coercion maps a whole family of non-answers onto 0 - `""`, `"  "`, `false`, `[]`, and `null` - which
 * then read as *explicitly Prohibited*, and `true` / `[1]` onto 1, reading as *explicitly
 * Authorized*. A questionnaire arriving with `stateID: ""` on every category therefore read as
 * fully resolved: the row painted Confirmed and Confirm All unblocked with nothing answered. An
 * allow-list needs no enumeration of the bad cases - anything not on it is not an answer.
 *
 * NotMentioned is deliberately absent: it is the one state that is explicitly NOT an answer.
 */
const ANSWERED_SENSITIVE_INFO_STATE_IDS: ReadonlySet<unknown> = new Set<unknown>([
    SensitiveInfoRelease.Authorized,
    SensitiveInfoRelease.Prohibited,
    String(SensitiveInfoRelease.Authorized),
    String(SensitiveInfoRelease.Prohibited),
]);

export function isSensitiveInfoResolved(data: readonly SensitiveInfoModel[]): boolean {
    // Security review L1/L4. Expressed as "EVERY category is explicitly Authorized or Prohibited"
    // rather than "no category is NotMentioned", against the allow-list above, for two reasons:
    //
    // 1. A numeric stateID is the PRECONDITION, checked rather than trusted. The strict
    //    `!== NotMentioned` form this replaced was only correct while that precondition held: given
    //    a string "2" nothing matched, so a WHOLLY UNANSWERED questionnaire read as resolved and
    //    this gate - the one separating "the AI declared these" from "the specialist decided these"
    //    - failed OPEN. (Round-3 review W4 found the evidence points the other way: the ten other
    //    stateID comparisons here are strict, several on load-bearing paths that work in production,
    //    so strings almost certainly never reach this component. The check is kept regardless
    //    because it is correct under either premise and cannot fail open, so no one has to settle
    //    that question to rely on this gate.)
    // 2. EVERY value outside the allow-list fails CLOSED - not just the null/undefined/NaN cases an
    //    earlier coercing version happened to name. For every consumer that means more redaction
    //    and a row the specialist must still answer, never a silent authorization.
    //
    // Behaviour is identical for well-formed numeric stateIDs, which is all any fixture or observed
    // payload carries.
    return data.length > 0 && data.every(item => ANSWERED_SENSITIVE_INFO_STATE_IDS.has(item.stateID));
}

/** The names of `ids`, in ascending id order, so a joined value is stable however the input was ordered. */
function sensitiveInfoNamesInIdOrder(data: readonly SensitiveInfoModel[], ids: ReadonlySet<SensitiveInfoType>): string[] {
    return data.filter(item => ids.has(item.id)).sort((a, b) => a.id - b.id).map(item => item.name);
}

/**
 * AB#37856. True when the Sensitive Information row's CURRENT Authorized set is EXACTLY the
 * set the extraction seeded (doc-sourced and unedited), so the status badge shows the green
 * Confirmed check rather than the blue "user-entered" icon. Any specialist add/remove
 * diverges the two sets. An empty AI match means nothing was seeded -> not doc-sourced.
 *
 * Only the AUTHORIZED set is compared, not every stateID. Under the 2026-08-12 seed behaviour
 * the undeclared categories are Prohibited by the SEED rather than by the specialist, so
 * comparing every stateID would compare the seed against itself and report doc-sourced for a
 * reason that has nothing to do with what the document said. The Authorized set is the only
 * half the extraction actually asserts, so it is the only half worth comparing - and a
 * specialist add/remove still diverges it, which is what the badge is for.
 *
 * Same shape as RecordsRequestedField's isRecordsRequestedDocSourced, and reuses the seed's
 * own matcher so the two can never drift.
 *
 * READ THIS AS "the current answer MATCHES the extraction", not "the AI seeded this". There is
 * no signal here that the seed actually ran, so a reopened transaction whose SAVED answers
 * happen to equal the declarations reports doc-sourced even though the seed was correctly
 * blocked by the existing-selection guard (DataEntryDetails.seedExtractedSensitiveInfo) - the
 * badge paints green and the correction records as accepted under Business Rule 8. Inherited
 * verbatim from isRecordsRequestedDocSourced, so it is consistent with precedent rather than a
 * new behavior; distinguishing the two would need seed provenance tracked in state.
 */
export function isSensitiveInfoDocSourced(
    declarations: readonly string[],
    data: readonly SensitiveInfoModel[],
): boolean {
    const matched = matchAiSensitiveInfoIds(declarations, data);
    if (matched.size === 0) {
        return false;
    }
    const authorized = new Set(data.filter(item => item.stateID === SensitiveInfoRelease.Authorized).map(item => item.id));
    if (authorized.size !== matched.size) {
        return false;
    }
    let allPresent = true;
    matched.forEach(id => {
        if (!authorized.has(id)) {
            allPresent = false;
        }
    });
    return allPresent;
}

/** The extraction's category-unspecified sentinel: a real assertion about the document, but not a category. */
const AI_SENSITIVE_INFO_DISCLOSED = "disclosed";

/**
 * AB#37856. The `aiValue` for the SensitiveInfoDeclarations correction. This used to be
 * hardcoded `null` (see QUICK_ENTRY_ROW_FIELD_NAMES' own comment in DataEntryDetails.tsx,
 * written when no row but Request Type/Purpose had AI wiring), which made every specialist
 * answer read to the correction-feedback layer as unprompted manual work.
 *
 * The matched categories' ROIS names when any matched; else the literal "Disclosed" when
 * that sentinel was declared (a real assertion worth recording even though it seeds
 * nothing); else null - "not asserted". A declaration outside the closed vocabulary is
 * DROPPED rather than echoed, because the enum is a PHI-channel control and free document
 * text must not be able to reach this payload if the upstream contract ever drifts.
 */
export function buildSensitiveInfoAiCorrectionValue(
    declarations: readonly string[],
    data: readonly SensitiveInfoModel[],
): string | null {
    const matched = matchAiSensitiveInfoIds(declarations, data);
    if (matched.size > 0) {
        return sensitiveInfoNamesInIdOrder(data, matched).join(", ");
    }
    // Security review L3: through the shared normalizer, not its own .trim() - this is the second
    // consumer, and guarding only the matcher would have left this throw path open.
    return declarations.some(value => normalizeAiDeclaration(value) === AI_SENSITIVE_INFO_DISCLOSED) ? "Disclosed" : null;
}

/**
 * AB#37856. The `SpecialistValue` for the SensitiveInfoDeclarations correction: the
 * currently Authorized categories' names, in the same ascending-id order the aiValue uses
 * so an accepted, unedited AI seed compares EQUAL and records as accepted rather than
 * edited (Business Rule 8 of QuickEntryCorrections.ts).
 *
 * Deliberately NOT the row's own `value` (mapSensitiveInfoToFieldGroupRow's "3 of 5
 * authorized" summary), which is what the correction used to submit - a count string can
 * never be compared against an AI value, so Rule 8 could not fire for this row.
 * "None" is the resolved-with-nothing-authorized sentinel; an unresolved row with nothing
 * authorized has no specialist answer yet, so it is null.
 */
export function buildSensitiveInfoSpecialistCorrectionValue(data: readonly SensitiveInfoModel[]): string | null {
    const authorized = new Set(data.filter(item => item.stateID === SensitiveInfoRelease.Authorized).map(item => item.id));
    if (authorized.size > 0) {
        return sensitiveInfoNamesInIdOrder(data, authorized).join(", ");
    }
    return isSensitiveInfoResolved(data) ? "None" : null;
}
